/**
 * AccountInfoContent
 *
 * Body of the AccountInfoSheet. Renders a vertical list of tappable rows
 * (icon + title + description) for the account-area entries that previously
 * lived in `HomeTopBarBody`. Uses the shared `ListItemTile` primitive so
 * that AccountInfoSheet looks like a visual sibling of `HelpAboutSheet`
 * (which uses the same primitive).
 *
 * The admin-gated Admin row (prepended first by `useAccountInfoItems`) opens
 * `AdminSheet` on tap and renders its icon in `theme.danger` (red). This
 * reproduces Flutter's type-keyed `_accountItemWidget` icon switch
 * (`account_info_bottom_sheet.dart:342-345`: `admin => activeRed`, all other
 * types `=> disabledColor`/grey) and matches the red tint the deleted
 * `HomeTopBarBody` admin row carried (`theme.danger`). Only the admin row is
 * tinted; every other row passes no `iconColor`, keeping `ListItemTile`'s
 * default `theme.icon.secondary` grey.
 *
 * Flutter source: lib/presentation/bottom_sheet/account_info_bottom_sheet.dart
 * (the `_onItemTap` switch at lines 122-173, the admin tap at lines 159-163,
 * and the icon tint at lines 342-345). Per-row descriptions also come from
 * that file (`_accountItems` at lines 33-82). Web routes every wired item
 * through the existing `Navigator.navigate*` methods; the admin row opens
 * `AdminSheet` (the web equivalent of Flutter's admin screen navigation).
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { Navigator } from '@/presentation/routing/Navigator';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';
import { AdminSheet } from '@/presentation/components/common/sheet/admin/AdminSheet';
import { AccountInfoType } from '@/domain/enums/accountInfo/accountInfoType';
import { useAccountInfoItems } from '../hooks/useAccountInfoItems';

export interface AccountInfoContentProps {
  /** Screen key forwarded to nested sheets (e.g., AdminSheet) */
  screenKey?: string;
}

export const AccountInfoContent: React.FC<AccountInfoContentProps> = ({ screenKey }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const items = useAccountInfoItems();

  const handleItemClick = (type: AccountInfoType) => {
    switch (type) {
      case AccountInfoType.ADMIN:
        AdminSheet.show({ screenKey });
        break;
      case AccountInfoType.WALLET:
        Navigator.navigateToWallet();
        break;
      case AccountInfoType.UNLOCKED_VIDEOS:
        Navigator.navigateToUnlockedVideos();
        break;
      case AccountInfoType.SUBSCRIPTIONS:
        Navigator.navigateToSubscriptions();
        break;
      case AccountInfoType.SUBSCRIBERS:
        Navigator.navigateToSubscribers();
        break;
      case AccountInfoType.LIKES:
        Navigator.navigateToLikedVideos();
        break;
      case AccountInfoType.WATCH_LATER:
        Navigator.navigateToWatchLater();
        break;
      case AccountInfoType.FAVOURITE_PEOPLE:
        Navigator.navigateToSavedUsers();
        break;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: responsive.spacing.xl,
        paddingRight: responsive.spacing.xl,
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing.lg,
      }}
    >
      {items.map((item) => (
        <ListItemTile
          key={item.type}
          title={item.title}
          description={item.description}
          iconPath={item.iconPath}
          iconColor={item.type === AccountInfoType.ADMIN ? theme.danger : undefined}
          onClick={() => handleItemClick(item.type)}
          qa={{ id: `account-info-${item.type}` }}
        />
      ))}
    </div>
  );
};
